/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSearch, faUserTie, faLightbulb, faChalkboardTeacher, 
  faChartLine, faHeart, faGraduationCap, faUsers, faUniversity,
  faRocket, faArrowUp, faCheckCircle, faEnvelope, faPhone, 
  faMapMarkerAlt, faPaperPlane, faBrain, faRobot, faGlobe, faChartPie,
  faPercentage, faFileExport, faMobileAlt, faBolt, faCalendarAlt,
  faBell, faShieldAlt, faDatabase, faLock, faCloud, faCode, faCogs,
  faBuilding, faHome, faHandsHelping
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp, faFacebook, faTwitter, faLinkedin, faInstagram } from '@fortawesome/free-brands-svg-icons';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import logoImage from '../assets/FINAL LOGO AK.png';
import Aurora from '../components/Aurora';
import AboutUs from './AboutUs';
import AboutName from './AboutName';
import AboutPlatformTable from '../components/AboutPlatformTable';
import '../styles/AboutPage.css';
import '../styles/AboutPageMobile.css';
import '../styles/AboutPlatformTables.css';

const AboutPlatform = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('platform'); // 'platform', 'name', or 'us'
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [stickyCtaVisible, setStickyCtaVisible] = useState(false);
  const [showTabSelector, setShowTabSelector] = useState(true);
  const [formData, setFormData] = useState({ name: '', email: '', subject: '', message: '' });
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [customHtml, setCustomHtml] = useState('');
  const [contentLoaded, setContentLoaded] = useState(false);

  // Check path and switch tab accordingly
  useEffect(() => {
    if (location.pathname === '/about') {
      setActiveTab('us');
    } else if (location.pathname === '/about-name') {
      setActiveTab('name');
    } else if (location.pathname === '/about-platform') {
      setActiveTab('platform');
    }
  }, [location.pathname]);

  // Component mounted - no need for CSS scroll-behavior, using custom JS animation

  // Load AOS (Animate On Scroll) library
  useEffect(() => {
    const loadAOS = async () => {
      // Load AOS CSS
      const link = document.createElement('link');
      link.href = 'https://unpkg.com/aos@2.3.1/dist/aos.css';
      link.rel = 'stylesheet';
      document.head.appendChild(link);

      // Load AOS JS
      const script = document.createElement('script');
      script.src = 'https://unpkg.com/aos@2.3.1/dist/aos.js';
      script.onload = () => {
        if (window.AOS) {
          window.AOS.init({
            duration: 1000,
            once: true,
            offset: 100
          });
        }
      };
      document.body.appendChild(script);

      return () => {
        document.head.removeChild(link);
        document.body.removeChild(script);
      };
    };
    
    loadAOS();
  }, []);

  // No need for manual particle creation anymore - Aurora handles the background

  // Scroll handlers
  useEffect(() => {
    let lastScrollY = window.pageYOffset;
    
    const handleScroll = () => {
      const scrollY = window.pageYOffset;
      setShowScrollTop(scrollY > 300);
      setStickyCtaVisible(scrollY > 200);
      
      // Hide tab selector when scrolling down, show when scrolling up or at top
      if (scrollY < 100) {
        setShowTabSelector(true);
      } else if (scrollY > lastScrollY && scrollY > 100) {
        // Scrolling down
        setShowTabSelector(false);
      } else if (scrollY < lastScrollY) {
        // Scrolling up
        setShowTabSelector(true);
      }
      
      lastScrollY = scrollY;
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Load custom content (if provided by admin)
  useEffect(() => {
    const fetchContent = async () => {
      try {
        const res = await axios.get(buildApiUrl('/api/public/about-platform'));
        if (res?.data?.contentHtml) {
          setCustomHtml(res.data.contentHtml);
        }
      } catch (e) {
        console.warn('About Platform content fetch failed, using default page.');
      } finally {
        setContentLoaded(true);
      }
    };
    fetchContent();
  }, []);

  // Form handlers
  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await axios.post(
        buildApiUrl('/api/contact/submit'),
        formData,
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data.success) {
        setShowSuccessMessage(response.data.message || 'Your message has been sent successfully! We\'ll get back to you soon.');
        setShowSuccessModal(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 3000);
      } else {
        setShowSuccessMessage('There was an error sending your message. Please try again.');
        setShowSuccessModal(true);
        setTimeout(() => {
          setShowSuccessModal(false);
        }, 3000);
      }
    } catch (error) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error.response?.data?.error || 'Failed to send message. Please try again later.';
      setShowSuccessMessage(errorMessage);
      setShowSuccessModal(true);
      setTimeout(() => {
        setShowSuccessModal(false);
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom smooth scroll function with animation
  const smoothScrollTo = (targetPosition, duration = 1000) => {
    const startPosition = window.pageYOffset;
    const distance = targetPosition - startPosition;
    let startTime = null;

    const easeInOutCubic = (t) => {
      return t < 0.5 
        ? 4 * t * t * t 
        : 1 - Math.pow(-2 * t + 2, 3) / 2;
    };

    const animation = (currentTime) => {
      if (startTime === null) startTime = currentTime;
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);
      
      const ease = easeInOutCubic(progress);
      window.scrollTo(0, startPosition + distance * ease);

      if (timeElapsed < duration) {
        requestAnimationFrame(animation);
      }
    };

    requestAnimationFrame(animation);
  };

  const scrollToTop = () => {
    smoothScrollTo(0, 800);
  };

  const scrollToRole = (roleId) => {
    const element = document.querySelector(`[data-role="${roleId}"]`);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 100;
      smoothScrollTo(offsetPosition, 1000);
    }
  };

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - 80;
      smoothScrollTo(offsetPosition, 1000);
    }
  };

  // If admin provided custom HTML, render it directly
  if (contentLoaded && customHtml && activeTab === 'platform') {
    return (
      <div className="expo-landing-page about-page-wrapper">
        <button className="back-to-home-btn" onClick={() => navigate('/')} title="Back to Home">
          <FontAwesomeIcon icon={faHome} />
        </button>
        {/* Tab Selector */}
        <div className={`about-tab-selector ${showTabSelector ? 'visible' : 'hidden'}`}>
          <button 
            className={`tab-btn ${activeTab === 'platform' ? 'active' : ''}`}
            onClick={() => { setActiveTab('platform'); navigate('/about-platform'); }}
          >
            About Platform
          </button>
          <button 
            className={`tab-btn ${activeTab === 'name' ? 'active' : ''}`}
            onClick={() => { setActiveTab('name'); navigate('/about-name'); }}
          >
            About Name
          </button>
          <button 
            className={`tab-btn ${activeTab === 'us' ? 'active' : ''}`}
            onClick={() => { setActiveTab('us'); navigate('/about'); }}
          >
            About Us
          </button>
        </div>
        <div className="custom-about-content" dangerouslySetInnerHTML={{ __html: customHtml }} />
      </div>
    );
  }

  // If About Name tab is selected, render AboutName component
  if (activeTab === 'name') {
    return (
      <div className="expo-landing-page about-page-wrapper about-name-active">
        <button className="back-to-home-btn" onClick={() => navigate('/')} title="Back to Home">
          <FontAwesomeIcon icon={faHome} />
        </button>
        {/* Tab Selector */}
        <div className={`about-tab-selector ${showTabSelector ? 'visible' : 'hidden'}`}>
          <button 
            className={`tab-btn ${activeTab === 'platform' ? 'active' : ''}`}
            onClick={() => { setActiveTab('platform'); navigate('/about-platform'); }}
          >
            About Platform
          </button>
          <button 
            className={`tab-btn ${activeTab === 'name' ? 'active' : ''}`}
            onClick={() => { setActiveTab('name'); navigate('/about-name'); }}
          >
            About Name
          </button>
          <button 
            className={`tab-btn ${activeTab === 'us' ? 'active' : ''}`}
            onClick={() => { setActiveTab('us'); navigate('/about'); }}
          >
            About Us
          </button>
        </div>
        <AboutName hideBackButton={true} />
      </div>
    );
  }

  // If About Us tab is selected, render AboutUs component
  if (activeTab === 'us') {
    return (
      <div className="expo-landing-page about-page-wrapper">
        <button className="back-to-home-btn" onClick={() => navigate('/')} title="Back to Home">
          <FontAwesomeIcon icon={faHome} />
        </button>
        {/* Tab Selector */}
        <div className={`about-tab-selector ${showTabSelector ? 'visible' : 'hidden'}`}>
          <button 
            className={`tab-btn ${activeTab === 'platform' ? 'active' : ''}`}
            onClick={() => { setActiveTab('platform'); navigate('/about-platform'); }}
          >
            About Platform
          </button>
          <button 
            className={`tab-btn ${activeTab === 'name' ? 'active' : ''}`}
            onClick={() => { setActiveTab('name'); navigate('/about-name'); }}
          >
            About Name
          </button>
          <button 
            className={`tab-btn ${activeTab === 'us' ? 'active' : ''}`}
            onClick={() => { setActiveTab('us'); navigate('/about'); }}
          >
            About Us
          </button>
        </div>
        <AboutUs hideBackButton={true} />
      </div>
    );
  }

  return (
    <div className="expo-landing-page about-page-wrapper about-platform-active">
      {/* Back to Home Button */}
      <button className="back-to-home-btn" onClick={() => navigate('/')} title="Back to Home">
        <FontAwesomeIcon icon={faHome} />
      </button>

      {/* Tab Selector */}
      <div className={`about-tab-selector ${showTabSelector ? 'visible' : 'hidden'}`}>
        <button 
          className={`tab-btn ${activeTab === 'platform' ? 'active' : ''}`}
          onClick={() => { setActiveTab('platform'); navigate('/about-platform'); }}
        >
          About Platform
        </button>
        <button 
          className={`tab-btn ${activeTab === 'name' ? 'active' : ''}`}
          onClick={() => { setActiveTab('name'); navigate('/about-name'); }}
        >
          About Name
        </button>
        <button 
          className={`tab-btn ${activeTab === 'us' ? 'active' : ''}`}
          onClick={() => { setActiveTab('us'); navigate('/about'); }}
        >
          About Us
        </button>
      </div>

      {/* Sticky CTA Bar */}
      <div className={`sticky-cta ${stickyCtaVisible ? 'show' : ''}`}>
        <div className="sticky-cta-content">
          <div className="sticky-cta-text">
            🚀 Join 500+ professionals already on AksharJobs! Limited early access spots available.
          </div>
          <div className="sticky-cta-buttons">
            <button onClick={scrollToContact} className="sticky-cta-btn">Get In Touch</button>
            <button onClick={() => navigate('/signup')} className="sticky-cta-btn primary">Get Early Access</button>
          </div>
        </div>
      </div>

      {/* Hero Section - Remade for Maximum Clarity */}
      <section id="home" className="about-hero-section">
        <div className="about-hero-bg">
          <Aurora
            colorStops={["#ff8c42", "#ffa726", "#14b8a6"]}
            blend={0.5}
            amplitude={1.0}
            speed={0.5}
          />
        </div>
        <div className="about-hero-wrapper">
          <div className="about-hero-left">
            <h1 className="about-main-title">
              Free AI-Powered Job Network
            </h1>
            <div className="about-badge-free">
              FREE WORLDWIDE
            </div>
            <p className="about-hero-desc">
              Revolutionizing recruitment with advanced AI, multilingual support, and cultural intelligence 
              designed specifically for the global market. Find the perfect match in minutes, not months.
            </p>
            
            <div className="about-hero-stats">
              <div className="about-stat-box">
                <div className="about-stat-num">95%</div>
                <div className="about-stat-label">AI Accuracy</div>
              </div>
              <div className="about-stat-box">
                <div className="about-stat-num">3x</div>
                <div className="about-stat-label">Faster</div>
              </div>
            </div>
            
            <div className="about-hero-cta">
              <button onClick={() => navigate('/signup')} className="about-cta-btn">
                <FontAwesomeIcon icon={faRocket} />
                Start Your Journey Now
              </button>
            </div>
          </div>
          
          <div className="about-hero-right">
            <div className="about-role-cards">
              <div className="about-role-card" onClick={() => scrollToRole('job-seeker')}>
                <FontAwesomeIcon icon={faSearch} />
                <span>Job Seekers</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('employer')}>
                <FontAwesomeIcon icon={faBuilding} />
                <span>Employers</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('intern')}>
                <FontAwesomeIcon icon={faGraduationCap} />
                <span>Interns</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('recruiter')}>
                <FontAwesomeIcon icon={faUserTie} />
                <span>Recruiters</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('mentor')}>
                <FontAwesomeIcon icon={faLightbulb} />
                <span>Mentors</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('trainer')}>
                <FontAwesomeIcon icon={faChalkboardTeacher} />
                <span>Trainers</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('consultant')}>
                <FontAwesomeIcon icon={faChartLine} />
                <span>Consultants</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('volunteer')}>
                <FontAwesomeIcon icon={faHeart} />
                <span>Volunteers</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('community')}>
                <FontAwesomeIcon icon={faUsers} />
                <span>Communities</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('university')}>
                <FontAwesomeIcon icon={faUniversity} />
                <span>Universities</span>
              </div>
              <div className="about-role-card" onClick={() => scrollToRole('ngo')}>
                <FontAwesomeIcon icon={faHandsHelping} />
                <span>NGOs</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Roles Section */}
      <section id="roles" className="roles-section">
        <div className="container">
          <div className="section-header">
            <h2>Roles & Benefits</h2>
            <p className="section-subtitle">How different users get value from AksharJobs</p>
          </div>

          <div className="roles-grid-detailed">
            <div className="role-card" data-role="job-seeker">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <h3 className="role-title">Job Seekers</h3>
                <p className="role-subtitle">AI-powered matching and career growth</p>
              </div>
              <ul className="benefits-list">
                <li>Smart matching to relevant jobs based on skills and experience</li>
                <li>Multilingual resume support (Swahili, English)</li>
                <li>Real-time alerts for new opportunities</li>
                <li>Interview prep and profile improvement tips</li>
                <li>Mobile-first experience on all devices</li>
              </ul>
            </div>

            <div className="role-card" data-role="employer">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                  <FontAwesomeIcon icon={faBuilding} />
                </div>
                <h3 className="role-title">Employers</h3>
                <p className="role-subtitle">Hire top talent and grow your business</p>
              </div>
              <ul className="benefits-list">
                <li>Access to a diverse pool of qualified candidates</li>
                <li>AI-powered candidate matching and recommendations</li>
                <li>Streamlined recruitment and hiring process</li>
                <li>Company branding and visibility tools</li>
                <li>Analytics and insights on recruitment performance</li>
              </ul>
            </div>

            <div className="role-card" data-role="intern">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faGraduationCap} />
                </div>
                <h3 className="role-title">Interns</h3>
                <p className="role-subtitle">Explore careers and build experience</p>
              </div>
              <ul className="benefits-list">
                <li>Best-fit internship matching</li>
                <li>Mentor connection and guidance</li>
                <li>Portfolio and certificate support</li>
                <li>Job placement assistance</li>
              </ul>
            </div>

            <div className="role-card" data-role="recruiter">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <h3 className="role-title">Recruiters</h3>
                <p className="role-subtitle">Find top talent faster with AI</p>
              </div>
              <ul className="benefits-list">
                <li>75.3% accurate candidate-job matching</li>
                <li>Powerful filters: skills, experience, location</li>
                <li>AI resume parsing and insights</li>
                <li>Interview scheduling and smart notifications</li>
                <li>Analytics on hiring funnel performance</li>
              </ul>
            </div>

            <div className="role-card" data-role="mentor">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faLightbulb} />
                </div>
                <h3 className="role-title">Mentors</h3>
                <p className="role-subtitle">Guide and inspire talent</p>
              </div>
              <ul className="benefits-list">
                <li>Connect with mentees aligned to your expertise</li>
                <li>Structured goals and progress tracking</li>
                <li>Resource library and community support</li>
                <li>Recognition for mentorship impact</li>
              </ul>
            </div>

            <div className="role-card" data-role="trainer">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                  <FontAwesomeIcon icon={faChalkboardTeacher} />
                </div>
                <h3 className="role-title">Trainers</h3>
                <p className="role-subtitle">Share knowledge and build skills</p>
              </div>
              <ul className="benefits-list">
                <li>Create and publish training programs</li>
                <li>Manage learners and track progress</li>
                <li>Issue certificates and credentials</li>
                <li>Analytics on learning outcomes and engagement</li>
                <li>Marketplace visibility to reach students</li>
              </ul>
            </div>

            <div className="role-card" data-role="consultant">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <h3 className="role-title">Consultants</h3>
                <p className="role-subtitle">Provide strategic guidance and expertise</p>
              </div>
              <ul className="benefits-list">
                <li>Connect with clients seeking specialized expertise</li>
                <li>Project management and collaboration tools</li>
                <li>Secure communication and file sharing</li>
                <li>Performance tracking and client feedback</li>
                <li>Professional network and referral system</li>
              </ul>
            </div>

            <div className="role-card" data-role="volunteer">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <h3 className="role-title">Volunteers</h3>
                <p className="role-subtitle">Make a positive impact in your community</p>
              </div>
              <ul className="benefits-list">
                <li>Connect with meaningful volunteer opportunities</li>
                <li>Track volunteer hours and impact</li>
                <li>Build skills through hands-on experience</li>
                <li>Network with like-minded professionals</li>
                <li>Receive recognition and certificates</li>
                <li>Access to exclusive volunteer events</li>
              </ul>
            </div>

            <div className="role-card" data-role="community">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <h3 className="role-title">Communities</h3>
                <p className="role-subtitle">Network, learn and collaborate</p>
              </div>
              <ul className="benefits-list">
                <li>Professional networking and forums</li>
                <li>Events, resources and knowledge sharing</li>
                <li>Collaboration and project opportunities</li>
                <li>Global and Kenya-based communities</li>
              </ul>
            </div>

            <div className="role-card" data-role="university">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #14b8a6, #0d9488)' }}>
                  <FontAwesomeIcon icon={faUniversity} />
                </div>
                <h3 className="role-title">Universities</h3>
                <p className="role-subtitle">Connect students with opportunities</p>
              </div>
              <ul className="benefits-list">
                <li>Student placement and career services</li>
                <li>Industry partnership opportunities</li>
                <li>Alumni network integration</li>
                <li>Research collaboration platforms</li>
                <li>Graduate employment tracking</li>
              </ul>
            </div>

            <div className="role-card" data-role="ngo">
              <div className="role-header">
                <div className="role-icon" style={{ background: 'linear-gradient(135deg, #ff6b35, #f97316)' }}>
                  <FontAwesomeIcon icon={faHandsHelping} />
                </div>
                <h3 className="role-title">NGOs</h3>
                <p className="role-subtitle">Drive social impact and create meaningful change</p>
              </div>
              <ul className="benefits-list">
                <li>Connect with passionate volunteers and professionals</li>
                <li>Post volunteer opportunities and project-based roles</li>
                <li>Access to skilled talent for social impact initiatives</li>
                <li>Partnership opportunities with other organizations</li>
                <li>Track volunteer engagement and impact metrics</li>
                <li>Build a network of supporters and advocates</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Complete Feature Overview</h2>
            <p className="section-subtitle">Discover all the powerful features that make AksharJobs the most advanced AI-powered job matching platform</p>
          </div>
          
          {/* AI & ML Features */}
          <div className="feature-category">
            <h3 className="category-title">🤖 AI & Machine Learning</h3>
            <div className="compact-features-grid">
              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faBrain} />
                </div>
                <div className="compact-feature-content">
                  <h4>Fine-Tuned SBERT Model</h4>
                  <p>75.3% accuracy in job-resume matching, outperforming industry standards by 15%</p>
                  <div className="compact-stats">
                    <span className="compact-stat">75.3%</span>
                    <span className="compact-stat">0.0068 MSE</span>
                  </div>
                  <div className="compact-highlight" data-tooltip="Our AI model achieves 75.3% accuracy, outperforming industry standards by 15%">Industry Leading</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faRobot} />
                </div>
                <div className="compact-feature-content">
                  <h4>Intelligent Resume Parsing</h4>
                  <p>Advanced AI extracts structured data from PDF/DOCX resumes using Google Gemini API</p>
                  <div className="compact-features">
                    <span className="compact-feature-tag">Skills</span>
                    <span className="compact-feature-tag">Education</span>
                    <span className="compact-feature-tag">Experience</span>
                  </div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="compact-feature-content">
                  <h4>Semantic Similarity Matching</h4>
                  <p>Goes beyond keyword matching to understand context and meaning in job descriptions</p>
                  <div className="compact-highlight" data-tooltip="Understands context and meaning, not just keywords, for better job-resume matching">Context-Aware</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faLightbulb} />
                </div>
                <div className="compact-feature-content">
                  <h4>AI Improvement Suggestions</h4>
                  <p>Personalized recommendations to help job seekers improve their profiles and applications</p>
                  <div className="compact-highlight" data-tooltip="AI-powered recommendations tailored to each job seeker's profile and goals">Personalized</div>
                </div>
              </div>
            </div>
          </div>

          {/* Multilingual & Cultural Features */}
          <div className="feature-category">
            <h3 className="category-title">🌍 Multilingual & Cultural Intelligence</h3>
            <div className="compact-features-grid">
              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faGlobe} />
                </div>
                <div className="compact-feature-content">
                  <h4>Multilingual Resume Processing</h4>
                  <p>Process resumes in Swahili, Sheng, local English, and other Kenyan languages</p>
                  <div className="compact-stats">
                    <span className="compact-stat">70% More Talent</span>
                  </div>
                  <div className="compact-features">
                    <span className="compact-feature-tag">Swahili</span>
                    <span className="compact-feature-tag">Sheng</span>
                    <span className="compact-feature-tag">Local English</span>
                  </div>
                  <div className="compact-highlight" data-tooltip="Exclusive multilingual support for Swahili, Sheng, and local languages - available only on AksharJobs">Unique Feature</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faHeart} />
                </div>
                <div className="compact-feature-content">
                  <h4>Cultural Intelligence</h4>
                  <p>Understand global business cultures, community dynamics, and local market preferences worldwide</p>
                  <div className="compact-features">
                    <span className="compact-feature-tag">Community Service</span>
                    <span className="compact-feature-tag">Local Business</span>
                    <span className="compact-feature-tag">Cultural Fit</span>
                  </div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="compact-feature-content">
                  <h4>Local Market Expertise</h4>
                  <p>Analyze formal vs informal sector experience and local business networks</p>
                  <div className="compact-highlight" data-tooltip="Specialized analysis of local business networks and market dynamics worldwide">Local Focus</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faUsers} />
                </div>
                <div className="compact-feature-content">
                  <h4>Community Integration</h4>
                  <p>Score candidates based on local network connections and community involvement</p>
                  <div className="compact-highlight" data-tooltip="Evaluates candidates based on community involvement and local network connections">Community-Focused</div>
                </div>
              </div>
            </div>
          </div>

          {/* Analytics & Insights */}
          <div className="feature-category">
            <h3 className="category-title">📊 Analytics & Insights</h3>
            <div className="compact-features-grid">
              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faChartPie} />
                </div>
                <div className="compact-feature-content">
                  <h4>Advanced Analytics Dashboard</h4>
                  <p>Comprehensive visualizations including match distribution, application status tracking, and diversity analytics</p>
                  <div className="compact-features">
                    <span className="compact-feature-tag">Match Distribution</span>
                    <span className="compact-feature-tag">Status Tracking</span>
                    <span className="compact-feature-tag">Diversity Analytics</span>
                  </div>
                  <div className="compact-highlight" data-tooltip="Comprehensive analytics and insights powered by real-time data for informed decisions">Data-Driven</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faPercentage} />
                </div>
                <div className="compact-feature-content">
                  <h4>Match Score Visualization</h4>
                  <p>Real-time calculation and visual representation of job-resume compatibility</p>
                  <div className="compact-stats">
                    <span className="compact-stat">80%+ Excellent</span>
                    <span className="compact-stat">60-79% Good</span>
                  </div>
                  <div className="compact-highlight" data-tooltip="Instant match score calculations and live updates as you browse opportunities">Real-Time</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faChartLine} />
                </div>
                <div className="compact-feature-content">
                  <h4>Performance Metrics</h4>
                  <p>Track hiring success rates, time-to-hire, and candidate satisfaction</p>
                  <div className="compact-highlight" data-tooltip="Monitor hiring metrics, success rates, and candidate satisfaction in real-time">Performance Tracking</div>
                </div>
              </div>

              <div className="compact-feature-card">
                <div className="compact-feature-icon">
                  <FontAwesomeIcon icon={faFileExport} />
                </div>
                <div className="compact-feature-content">
                  <h4>Export & Reporting</h4>
                  <p>Generate detailed reports and export data for further analysis</p>
                  <div className="compact-highlight" data-tooltip="Export detailed reports and analytics data in multiple formats for further analysis">Export Ready</div>
                </div>
              </div>
            </div>
          </div>

          {/* User Experience Features */}
          <div className="feature-category">
            <h3 className="category-title">👥 User Experience & Interface</h3>
            <div className="features-grid">
              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faUserTie} />
                </div>
                <h3>Role-Based Dashboards</h3>
                <p>Separate, optimized interfaces for job seekers and recruiters</p>
                <ul className="feature-list">
                  <li>Job Seeker Dashboard</li>
                  <li>Recruiter Dashboard</li>
                  <li>Customized workflows</li>
                  <li>Role-specific features</li>
                </ul>
              </div>

              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faSearch} />
                </div>
                <h3>Advanced Job Search</h3>
                <p>Powerful filtering and search capabilities</p>
                <ul className="feature-list">
                  <li>Location-based filtering</li>
                  <li>Job type filtering</li>
                  <li>Experience level filtering</li>
                  <li>Skills-based search</li>
                  <li>Salary range filtering</li>
                </ul>
              </div>

              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faMobileAlt} />
                </div>
                <h3>Cross-Device Compatibility</h3>
                <p>Fully responsive design that works perfectly on all devices and screen sizes</p>
                <ul className="feature-list">
                  <li>Mobile phones (iOS & Android)</li>
                  <li>Tablets (iPad, Android tablets)</li>
                  <li>Desktop computers (Windows, Mac)</li>
                  <li>Laptops (all screen sizes)</li>
                  <li>Smart TVs and large displays</li>
                </ul>
                <div className="feature-highlight">Universal Access</div>
              </div>

              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faBolt} />
                </div>
                <h3>Real-Time Updates</h3>
                <p>Instant notifications and real-time status updates</p>
                <div className="feature-highlight">Live Updates</div>
              </div>

              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
                <h3>Interview Scheduling</h3>
                <p>Built-in calendar system for scheduling interviews</p>
                <ul className="feature-list">
                  <li>Calendar integration</li>
                  <li>Automated reminders</li>
                  <li>Time zone support</li>
                  <li>Rescheduling options</li>
                </ul>
              </div>

              <div className="feature-card rolling-card">
                <div className="feature-icon">
                  <FontAwesomeIcon icon={faBell} />
                </div>
                <h3>Smart Notifications</h3>
                <p>Intelligent notification system for relevant updates</p>
                <div className="feature-highlight">Smart Alerts</div>
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about">
        <div className="container">
          <div className="about-content">
            <div className="about-text">
              <h2>Revolutionizing Recruitment Worldwide</h2>
              <p className="about-description">
                AksharJobs is not just another job portal. We're a comprehensive AI-powered platform 
                that breaks down language barriers, understands cultural nuances, and connects the right 
                talent with the right opportunities across the globe.
              </p>
              <div className="about-features">
                <div className="about-feature">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Access to 70% more talent through multilingual support</span>
                </div>
                <div className="about-feature">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Cultural intelligence for better candidate-company fit</span>
                </div>
                <div className="about-feature">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Advanced AI with industry-leading 75.3% accuracy</span>
                </div>
                <div className="about-feature">
                  <FontAwesomeIcon icon={faCheckCircle} />
                  <span>Comprehensive analytics for data-driven decisions</span>
                </div>
              </div>
            </div>
            <div className="about-visual">
              <div className="global-impact">
                <div className="impact-item">
                  <FontAwesomeIcon icon={faGlobe} />
                  <span className="impact-number">5+</span>
                  <span className="impact-label">Countries</span>
                </div>
                <div className="impact-item">
                  <FontAwesomeIcon icon={faGlobe} />
                  <span className="impact-number">3+</span>
                  <span className="impact-label">Languages</span>
                </div>
                <div className="impact-item">
                  <FontAwesomeIcon icon={faUsers} />
                  <span className="impact-number">100+</span>
                  <span className="impact-label">Job Matches</span>
                </div>
                <div className="impact-item">
                  <FontAwesomeIcon icon={faChartLine} />
                  <span className="impact-number">85%</span>
                  <span className="impact-label">Success Rate</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Get In Touch</h2>
            <p className="section-subtitle">Ready to revolutionize your hiring process? Let's talk!</p>
          </div>
          
          <div className="contact-content">
            <div className="contact-info">
              <div className="contact-item">
                <FontAwesomeIcon icon={faEnvelope} />
                <div>
                  <h4>Email Us</h4>
                  <p><a href="mailto:info@aksharjobs.com">info@aksharjobs.com</a></p>
                </div>
              </div>
              <div className="contact-item">
                <FontAwesomeIcon icon={faPhone} />
                <div>
                  <h4>Call Us</h4>
                  <p><a href="tel:+254743599050">+254 743 599050</a></p>
                </div>
              </div>
              <div className="contact-item">
                <FontAwesomeIcon icon={faMapMarkerAlt} />
                <div>
                  <h4>Visit Us</h4>
                  <p>PRJ8+MRR, Swaminarayan Road, Laxcon Court and Plaza , 2nd floor , Nairobi</p>
                </div>
              </div>
              <div className="contact-item">
                <FontAwesomeIcon icon={faWhatsapp} />
                <div>
                  <h4>WhatsApp</h4>
                  <p><a href="https://wa.me/254743599050" target="_blank" rel="noopener noreferrer">+254 743 599050</a></p>
                </div>
              </div>
            </div>
            
            <div className="contact-form-container">
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="name" 
                    placeholder="Your Name" 
                    value={formData.name}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="email" 
                    name="email" 
                    placeholder="Your Email" 
                    value={formData.email}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <input 
                    type="text" 
                    name="subject" 
                    placeholder="Subject" 
                    value={formData.subject}
                    onChange={handleInputChange}
                    required 
                  />
                </div>
                <div className="form-group">
                  <textarea 
                    name="message" 
                    placeholder="Your Message" 
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <button type="submit" className="btn-primary" disabled={isSubmitting}>
                  <FontAwesomeIcon icon={faPaperPlane} />
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src={logoImage} alt="AksharJobs Logo" className="logo-img" />
              <span className="logo-text">AksharJobs</span>
            </div>
            <div className="footer-links">
              <a href="#home" onClick={(e) => { 
                e.preventDefault(); 
                const el = document.getElementById('home');
                if (el) {
                  const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
                  smoothScrollTo(pos, 1000);
                }
              }}>Home</a>
              <a href="/about-name" onClick={(e) => { e.preventDefault(); navigate('/about-name'); }}>About Name</a>
              <a href="#features" onClick={(e) => {
                e.preventDefault();
                const el = document.getElementById('features');
                if (el) {
                  const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
                  smoothScrollTo(pos, 1000);
                }
              }}>Features</a>
              <a href="#about" onClick={(e) => {
                e.preventDefault(); 
                const el = document.getElementById('about');
                if (el) {
                  const pos = el.getBoundingClientRect().top + window.pageYOffset - 80;
                  smoothScrollTo(pos, 1000);
                }
              }}>About</a>
              <a href="#contact" onClick={(e) => { 
                e.preventDefault(); 
                scrollToContact();
              }}>Contact</a>
            </div>
            <div className="footer-social">
              <a href="#" onClick={(e) => e.preventDefault()}><FontAwesomeIcon icon={faFacebook} /></a>
              <a href="#" onClick={(e) => e.preventDefault()}><FontAwesomeIcon icon={faTwitter} /></a>
              <a href="#" onClick={(e) => e.preventDefault()}><FontAwesomeIcon icon={faLinkedin} /></a>
              <a href="#" onClick={(e) => e.preventDefault()}><FontAwesomeIcon icon={faInstagram} /></a>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 AksharJobs. All rights reserved.</p>
          </div>
        </div>
      </footer>

      {/* WhatsApp Floating Button */}
      <div className="whatsapp-float">
        <a 
          href="https://wa.me/254743599050?text=Hi! I'm interested in AksharJobs. Can you tell me more about the platform?" 
          className="whatsapp-btn" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={faWhatsapp} />
        </a>
      </div>

      {/* Scroll Up Arrow */}
      <div className={`scroll-up-arrow ${showScrollTop ? 'show' : ''}`}>
        <button className="scroll-up-btn" onClick={scrollToTop}>
          <FontAwesomeIcon icon={faArrowUp} />
        </button>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="modal" style={{ display: 'block' }}>
          <div className="modal-content success-modal">
            <div className="success-icon">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h2>Success!</h2>
            <p>{showSuccessMessage}</p>
            <button className="btn-primary" onClick={() => setShowSuccessModal(false)}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AboutPlatform;
